import React from 'react'
import PropTypes from 'prop-types'
import isEmpty from 'lodash/isEmpty'
import styled, { css } from 'styled-components'
import ExpanderGroup from 'components/common/ExpanderGroup'
import Link, { A } from 'components/common/Link/Link'
import Media from 'components/common/Media'
import Mixin from 'components/common/Mixin'
import Theme from 'components/common/Theme/Theme'
import WarrantyBadge, { WarrantyBadgeTitle } from 'components/common/WarrantyBadge'
import { H2 } from 'components/common/Text/Text'
import TireSpecification from '../../components/TireSpecification'
import TireRating from '../../components/TireRating'
import { arrayHasValues, isInventoryStatusLoaded } from 'helpers/utils'
import PromotionTeaser from '../../components/PromotionTeaser'
import { RECOMMENDED_PRODUCTS_MINIMUM_COUNT } from '../constants'
import FavoriteButton from '../../../MyFavorites/FavoriteButton'
import ProductFlag from 'components/common/ProductFlag/ProductFlag'
import {
  OE_TOOLTIP_MESSAGE,
  OE_FLAG_TYPE,
  OE_FLAG_NAME,
  EVS_FLAG_TYPE,
  EVS_FLAG_NAME,
  EVS_TOOLTIP_MESSAGE,
  EVS_FLAG_ICON_NAME,
  EVC_FLAG_TYPE,
  EVC_FLAG_NAME,
  EVC_TOOLTIP_MESSAGE,
  EVC_FLAG_ICON_NAME
} from 'components/common/ProductFlag/constans'
import TireAvailability from 'components/common/TireAvailability'
import { INVENTORY_STATUS_LOADED, ADDING_TO_SHOPPING_QUOTE } from 'components/common/UpdatingBlock/constants'
import useSERPTiresAvailabilityStatus from 'src/hooks/useSERPTiresAvailabilityStatus.js'
import UpdatingBlock from 'components/common/UpdatingBlock'
import CompatibilityLabel from 'components/common/CompatibilityLabel'

const ProductHeader = ({
  storeNumber,
  inventoryStatuses,
  isPilotStore,
  bvId,
  brandName,
  expanderMenu,
  reviewsLink,
  tireDetailUrl,
  sizeFront,
  sizeRear,
  srcTire,
  srcLogo,
  skuId,
  title,
  countFront,
  countRear,
  warrantyBadges: [{
    name: warrantyName,
    value: warrantyValue
  } = {}] = [],
  recommendedCount,
  specifications,
  promotions = [],
  handleProductClick = () => {},
  isOeItem,
  isEvItem,
  itemEVType,
  availability = {},
  compatibilityState,
  vehicleName
}) => {
  /* this custom hook is designed to make inventory service call when quantity is updated on the SERP */
  const [availabilityForQtyChange] = useSERPTiresAvailabilityStatus({
    countFront,
    brandName,
    frontTireBvId: bvId,
    storeNumber,
    dependencies: [countFront, isPilotStore],
    productSkuId: skuId,
    isPilotStore
  })
  const renderTireAvailability = (
    <AvailabilityWrapper
      name={ INVENTORY_STATUS_LOADED }
      value={ skuId }
      condition={ isInventoryStatusLoaded(inventoryStatuses, skuId) }>
      <TireAvailability availability={ !isEmpty(availabilityForQtyChange) ? availabilityForQtyChange : availability } />
    </AvailabilityWrapper>
  )

  return <UpdatingBlock
    name={ ADDING_TO_SHOPPING_QUOTE }
    value={ skuId } >
    <Block data-at-product-header={ title } recommendedCount={ recommendedCount }>
      <Table>
        <Link to={ tireDetailUrl } onClick={ handleProductClick }>
          <TireImageWrapper>
            <Media.Desktop>
              <Tire src={ `${srcTire}?$BOT-TireSearchResult-Tire-Mobile-w86$` } alt="Navigate to product details page" />
            </Media.Desktop>
            <Media.Mobile>
              <Tire src={ `${srcTire}?$BOT-TireSearchResult-Tire-Mobile-w86$` } alt="Navigate to product details page" />
            </Media.Mobile>
          </TireImageWrapper>
        </Link>
        <LogoDetailsWrapper>
          <div>
            <CustomLink
              data-at-recommended-product-link
              bolder
              to={ tireDetailUrl }
              onClick={ handleProductClick }>
              <LogoWrapper data-at-recommended-product-logo>
                <Logo src={ `${srcLogo}?$BOT-Tire-Search-Result-Brand-Desktop-h30$` } alt={ brandName } />
              </LogoWrapper>
            </CustomLink>
          </div>
          <div>
            <H2 noMargin>
              <CustomLink
                data-at-recommended-product-link
                bolder
                to={ tireDetailUrl }
                onClick={ handleProductClick }>
                { title }
              </CustomLink>
            </H2>
            <SizeTitle>
              <Size data-at-front-size>
                { sizeRear && <TitleTire>Front</TitleTire> }
                { sizeFront }
              </Size>
              { sizeRear &&
                <Size data-at-rear-size>
                  <TitleTire>Rear</TitleTire>
                  { sizeRear }
                </Size>
              }
              { (isOeItem || isEvItem) &&
                <ProductFlagWrapper>
                  { isOeItem &&
                  <ProductFlag
                    flagType={ OE_FLAG_TYPE }
                    name={ OE_FLAG_NAME }
                    tooltipMessage={ OE_TOOLTIP_MESSAGE }
                    gbb />
                  }
                  { (isEvItem && itemEVType === EVS_FLAG_TYPE) &&
                    <ProductFlag
                      flagType={ EVS_FLAG_ICON_NAME }
                      tooltipMessage={ EVS_TOOLTIP_MESSAGE }
                      name={ EVS_FLAG_NAME }
                      gbb />
                  }
                  { (isEvItem && itemEVType === EVC_FLAG_TYPE) &&
                    <ProductFlag
                      flagType={ EVC_FLAG_ICON_NAME }
                      tooltipMessage={ EVC_TOOLTIP_MESSAGE }
                      name={ EVC_FLAG_NAME }
                      gbb />
                  }
                </ProductFlagWrapper>
              }
            </SizeTitle>
          </div>

          <FavoriteButtonHeart
            skuId={ skuId }
            countFront={ countFront }
            countRear={ countRear }
            tireLineName={ title }
            imageUrl={ srcTire }
            brandLogoUrl={ srcLogo }
            frontTireSize={ sizeFront }
            rearTireSize={ sizeRear } />
        </LogoDetailsWrapper>
      </Table>
      <CustomTireRating
        bvId={ bvId }
        reviewName={ title }
        reviewsLink={ reviewsLink }
        small />
      {
        Boolean(compatibilityState) &&
        <CompatibilityWrapper>
          <CompatibilityLabel dense status={ compatibilityState } vehicle={ vehicleName } />
        </CompatibilityWrapper>
      }
      <WarrantyBlock>
        <IconWrapper>
          <WarrantyBadge name={ warrantyName } warranty={ warrantyValue } />
        </IconWrapper>
        <WarrantyBadgeTitle name={ warrantyName } value={ warrantyValue } />
      </WarrantyBlock>
      <StyledExpanderGroup menu={ expanderMenu } recommendedCount={ recommendedCount }>
        { arrayHasValues(promotions) && (
          <ExpanderButtonContent>
            <PromotionTeaser promotions={ promotions } />
          </ExpanderButtonContent>
        )}
        <ExpanderButtonContent>
          <TireSpecification specification={ specifications } title={ sizeRear ? 'Front' : '' } clear hideCaption />
          {/* TODO use real tire rear specifications */}
          {
            sizeRear &&
            <TireSpecification specification={ specifications } title={ 'Rear' } hideCaption clear />
          }
        </ExpanderButtonContent>
      </StyledExpanderGroup>
      {renderTireAvailability}
    </Block>
  </UpdatingBlock>
}

ProductHeader.propTypes = {
  availability: PropTypes.shape(),
  brandName: PropTypes.string,
  bvId: PropTypes.string,
  compatibilityState: PropTypes.string,
  countFront: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  countRear: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  expanderMenu: PropTypes.arrayOf(PropTypes.shape()),
  handleProductClick: PropTypes.func,
  inventoryStatuses: PropTypes.arrayOf(PropTypes.shape()),
  isEvItem: PropTypes.bool,
  isOeItem: PropTypes.bool,
  isPilotStore: PropTypes.bool,
  itemEVType: PropTypes.string,
  promotions: PropTypes.arrayOf(PropTypes.shape()),
  recommendedCount: PropTypes.number,
  reviewsLink: PropTypes.string,
  sizeFront: PropTypes.string,
  sizeRear: PropTypes.string,
  skuId: PropTypes.string,
  specifications: PropTypes.arrayOf(PropTypes.shape()),
  srcLogo: PropTypes.string,
  srcTire: PropTypes.string,
  storeNumber: PropTypes.string,
  tireDetailUrl: PropTypes.string,
  title: PropTypes.string,
  vehicleName: PropTypes.string,
  warrantyBadges: PropTypes.arrayOf(PropTypes.shape())
}

export default ProductHeader

const IconWrapper = styled.div`
  display: inline-block;
  min-height: 55px;
  text-align: center;
  margin-right: 10px;
`


const TitleTire = styled.div`
  text-transform: uppercase;
  line-height: 12px;
  ${Mixin.font.GothamBold(10)}
`

const SizeTitle = styled.div`
  margin-top: 10px;
  display: flex;
  align-items: center;
`

const Size = styled.div`
  line-height: normal;
  text-transform: uppercase;
  margin-right: 10px;
  color: ${Theme.colors.black_new};
  ${Mixin.font.GothamBold(14)}
  ${Media.mobile`
    display: inline-block;
  `}
  ${props => props.inline && css`
    display: inline;
  `}
`

const Table = styled.div`
  display: table;
  width: 100%;
  margin: 0 0 15px;
`

const WarrantyBlock = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
  padding: 5px 10px 15px 10px;
`

const TireImageWrapper = styled.div`
  display: table-cell;
  vertical-align: top;
  min-height: 80px;
  width: 32%;
  padding: 0 5px 5px 0;
`

const LogoDetailsWrapper = styled.div`
  position: relative;
  display: table-cell;
  vertical-align: top;
  min-height: 80px;
  width: 68%;
  padding-top: 10px;
`

const FavoriteButtonHeart = styled(FavoriteButton)`
  position: absolute;
  top: -30px;
  right: 0;
`

const Tire = styled.img`
  max-width: 100%;
  min-width: 42px;
  margin: 0 auto;
  position: relative;
`

const Logo = styled.img`
  max-width: 140px;
  cursor: pointer;
`

const LogoWrapper = styled.div`
  min-height: 25px;
`

const CustomLink = styled(Link)`
  display: inline-block;
  margin-bottom: 5px;
  font-size: 16px;
`

const CustomTireRating = styled(TireRating)`
  width: 100%;
  margin: 0 0 10px;
`

const ExpanderButtonContent = styled.div`
  padding: 0;
`

const StyledExpanderGroup = styled(ExpanderGroup)`
  ${props => props.recommendedCount > RECOMMENDED_PRODUCTS_MINIMUM_COUNT && css`
    ${Media.tablet`
      [data-at-expander-wrapper] {
        &:first-child {
          margin-right: 5px;
        }
        &:not(:first-child) {
          padding-left: 5px;
        }
      }
      ${A} {
        font-size: 12px;
      }
    `}
  `}
`

const Block = styled.div`
  padding: 37px 10px 1px;
  background-color: #fff;
  ${props => props.recommendedCount > RECOMMENDED_PRODUCTS_MINIMUM_COUNT && css`
    ${Media.tablet`
      padding: 37px 5px 1px;
    `}
  `}
`

const ProductFlagWrapper = styled.div`
  display: flex;
  align-items: center;

  & > span:first-of-type {
    margin-left: 0;
  }
`
const AvailabilityWrapper = styled(UpdatingBlock)`
  margin-bottom: 20px;
`

const CompatibilityWrapper = styled.div`
  padding: 15px 0 0;
`
